/**
 * WiseSwap i18n — Lightweight internationalization
 *
 * Auto-detects browser language, supports manual override via localStorage.
 * t('key') returns the translated string for the current locale.
 */

const SUPPORTED_LOCALES = ['en', 'es', 'zh', 'fr', 'de', 'ja', 'ko', 'pt', 'ru', 'ar', 'tr', 'vi'];

const translations = {
    en: {
        // Header
        swap: 'Swap',
        pools: 'Pools',
        connect: 'Connect',
        // Swap Card
        youPay: 'You Pay',
        youReceive: 'You Receive',
        balance: 'Balance',
        enterAmount: 'Enter an amount',
        selectToken: 'Select token',
        swapBtn: 'Swap',
        findingRoute: 'Finding best route...',
        noRouteFound: 'No route found',
        balanceNotEnough: 'Balance Not Enough',
        connectWallet: 'Connect Wallet',
        // Swap Info
        rate: 'Rate',
        interfaceFee: 'Interface Fee',
        feeTooltip: 'No interface fee — 0 bps',
        priceImpact: 'Price Impact',
        minReceived: 'Min. Received',
        networkCost: 'Network Cost',
        none: 'None',
        // Settings
        settings: 'Settings',
        slippageTolerance: 'Slippage Tolerance',
        customPercent: 'Custom %',
        txDeadline: 'Transaction Deadline',
        minutes: 'minutes',
        developer: 'Developer',
        showTestnets: 'Show Testnets',
        // Notifications
        submittingSwap: '🔄 Submitting swap transaction...',
        swapSubmitted: '✅ Swap submitted!',
        txCount: 'transaction(s)',
        connectPrompt: 'Please connect your wallet to swap',
        connected: 'Connected',
        // Footer
        routingOptimized: 'Routing optimized across Uniswap V2 · V3 · V4',
        noFees: 'No interface fees',
        disclaimer: 'Buy and sell cryptocurrencies at your own risk. Always Do Your Own Research.',
        // Language
        language: 'Language',
        // Meta
        metaTitle: 'WiseSwap — Multi-Version Uniswap Router',
        metaDescription: 'WiseSwap — swap tokens with optimal routing across Uniswap V2, V3, and V4 pools.',
    },

    es: {
        swap: 'Intercambiar',
        pools: 'Pools',
        connect: 'Conectar',
        youPay: 'Tú pagas',
        youReceive: 'Tú recibes',
        balance: 'Saldo',
        enterAmount: 'Ingresa una cantidad',
        selectToken: 'Seleccionar token',
        swapBtn: 'Intercambiar',
        findingRoute: 'Buscando la mejor ruta...',
        noRouteFound: 'No se encontró ruta',
        balanceNotEnough: 'Saldo insuficiente',
        connectWallet: 'Conectar billetera',
        rate: 'Tasa',
        interfaceFee: 'Tarifa de interfaz',
        feeTooltip: 'Sin tarifa de interfaz — 0 bps',
        priceImpact: 'Impacto en precio',
        minReceived: 'Mínimo recibido',
        networkCost: 'Costo de red',
        none: 'Ninguna',
        settings: 'Configuración',
        slippageTolerance: 'Tolerancia de deslizamiento',
        customPercent: 'Personalizado %',
        txDeadline: 'Fecha límite de transacción',
        minutes: 'minutos',
        developer: 'Desarrollador',
        showTestnets: 'Mostrar Testnets',
        submittingSwap: '🔄 Enviando transacción...',
        swapSubmitted: '✅ ¡Intercambio enviado!',
        txCount: 'transacción(es)',
        connectPrompt: 'Conecta tu billetera para intercambiar',
        connected: 'Conectado',
        routingOptimized: 'Ruteo optimizado en Uniswap V2 · V3 · V4',
        noFees: 'Sin tarifas de interfaz',
        disclaimer: 'Compra y vende criptomonedas bajo tu propio riesgo. Siempre investiga por tu cuenta.',
        language: 'Idioma',
        metaTitle: 'WiseSwap — Enrutador Multi-Versión de Uniswap',
        metaDescription: 'WiseSwap — intercambia tokens con ruteo óptimo a través de pools de Uniswap V2, V3 y V4.',
    },

    zh: {
        swap: '兑换',
        pools: '流动池',
        connect: '连接',
        youPay: '您支付',
        youReceive: '您收到',
        balance: '余额',
        enterAmount: '输入金额',
        selectToken: '选择代币',
        swapBtn: '兑换',
        findingRoute: '正在寻找最佳路线...',
        noRouteFound: '未找到路线',
        balanceNotEnough: '余额不足',
        connectWallet: '连接钱包',
        rate: '汇率',
        interfaceFee: '界面费用',
        feeTooltip: '无界面费用 — 0 基点',
        priceImpact: '价格影响',
        minReceived: '最少收到',
        networkCost: '网络费用',
        none: '无',
        settings: '设置',
        slippageTolerance: '滑点容忍度',
        customPercent: '自定义 %',
        txDeadline: '交易截止时间',
        minutes: '分钟',
        developer: '开发者',
        showTestnets: '显示测试网',
        submittingSwap: '🔄 提交兑换交易中...',
        swapSubmitted: '✅ 兑换已提交！',
        txCount: '笔交易',
        connectPrompt: '请连接钱包以进行兑换',
        connected: '已连接',
        routingOptimized: '跨 Uniswap V2 · V3 · V4 的最优路由',
        noFees: '无界面费用',
        disclaimer: '买卖加密货币风险自负，请务必自行研究。',
        language: '语言',
        metaTitle: 'WiseSwap — Uniswap 多版本路由器',
        metaDescription: 'WiseSwap — 通过 Uniswap V2、V3 和 V4 流动池进行最优路由代币兑换。',
    },

    fr: {
        swap: 'Échanger',
        pools: 'Pools',
        connect: 'Connecter',
        youPay: 'Vous payez',
        youReceive: 'Vous recevez',
        balance: 'Solde',
        enterAmount: 'Entrez un montant',
        selectToken: 'Sélectionner un jeton',
        swapBtn: 'Échanger',
        findingRoute: 'Recherche de la meilleure route...',
        noRouteFound: 'Aucune route trouvée',
        balanceNotEnough: 'Solde insuffisant',
        connectWallet: 'Connecter le portefeuille',
        rate: 'Taux',
        interfaceFee: 'Frais d\'interface',
        feeTooltip: 'Pas de frais d\'interface — 0 bps',
        priceImpact: 'Impact sur le prix',
        minReceived: 'Min. reçu',
        networkCost: 'Coût réseau',
        none: 'Aucun',
        settings: 'Paramètres',
        slippageTolerance: 'Tolérance au glissement',
        customPercent: 'Personnalisé %',
        txDeadline: 'Délai de transaction',
        minutes: 'minutes',
        developer: 'Développeur',
        showTestnets: 'Afficher les Testnets',
        submittingSwap: '🔄 Soumission de la transaction...',
        swapSubmitted: '✅ Échange soumis !',
        txCount: 'transaction(s)',
        connectPrompt: 'Connectez votre portefeuille pour échanger',
        connected: 'Connecté',
        routingOptimized: 'Routage optimisé via Uniswap V2 · V3 · V4',
        noFees: 'Pas de frais d\'interface',
        disclaimer: 'Achetez et vendez des cryptomonnaies à vos propres risques. Faites toujours vos propres recherches.',
        language: 'Langue',
        metaTitle: 'WiseSwap — Routeur Multi-Version Uniswap',
        metaDescription: 'WiseSwap — échangez des jetons avec un routage optimal via les pools Uniswap V2, V3 et V4.',
    },

    de: {
        swap: 'Tauschen',
        pools: 'Pools',
        connect: 'Verbinden',
        youPay: 'Sie zahlen',
        youReceive: 'Sie erhalten',
        balance: 'Guthaben',
        enterAmount: 'Betrag eingeben',
        selectToken: 'Token auswählen',
        swapBtn: 'Tauschen',
        findingRoute: 'Beste Route wird gesucht...',
        noRouteFound: 'Keine Route gefunden',
        balanceNotEnough: 'Guthaben nicht ausreichend',
        connectWallet: 'Wallet verbinden',
        rate: 'Kurs',
        interfaceFee: 'Schnittstellengebühr',
        feeTooltip: 'Keine Schnittstellengebühr — 0 bps',
        priceImpact: 'Preisauswirkung',
        minReceived: 'Min. erhalten',
        networkCost: 'Netzwerkkosten',
        none: 'Keine',
        settings: 'Einstellungen',
        slippageTolerance: 'Slippage-Toleranz',
        customPercent: 'Benutzerdefiniert %',
        txDeadline: 'Transaktionsfrist',
        minutes: 'Minuten',
        developer: 'Entwickler',
        showTestnets: 'Testnets anzeigen',
        submittingSwap: '🔄 Transaktion wird übermittelt...',
        swapSubmitted: '✅ Tausch übermittelt!',
        txCount: 'Transaktion(en)',
        connectPrompt: 'Wallet verbinden zum Tauschen',
        connected: 'Verbunden',
        routingOptimized: 'Routing optimiert über Uniswap V2 · V3 · V4',
        noFees: 'Keine Schnittstellengebühren',
        disclaimer: 'Kryptowährungen kaufen und verkaufen auf eigenes Risiko. Recherchieren Sie immer selbst.',
        language: 'Sprache',
        metaTitle: 'WiseSwap — Multi-Version Uniswap Router',
        metaDescription: 'WiseSwap — Token tauschen mit optimiertem Routing über Uniswap V2, V3 und V4 Pools.',
    },

    ja: {
        swap: 'スワップ',
        pools: 'プール',
        connect: '接続',
        youPay: '支払い',
        youReceive: '受取り',
        balance: '残高',
        enterAmount: '金額を入力',
        selectToken: 'トークンを選択',
        swapBtn: 'スワップ',
        findingRoute: '最適なルートを検索中...',
        noRouteFound: 'ルートが見つかりません',
        balanceNotEnough: '残高不足',
        connectWallet: 'ウォレットを接続',
        rate: 'レート',
        interfaceFee: 'インターフェース手数料',
        feeTooltip: 'インターフェース手数料なし — 0 bps',
        priceImpact: '価格への影響',
        minReceived: '最低受取量',
        networkCost: 'ネットワークコスト',
        none: 'なし',
        settings: '設定',
        slippageTolerance: 'スリッページ許容度',
        customPercent: 'カスタム %',
        txDeadline: '取引期限',
        minutes: '分',
        developer: '開発者',
        showTestnets: 'テストネットを表示',
        submittingSwap: '🔄 スワップ送信中...',
        swapSubmitted: '✅ スワップ送信完了！',
        txCount: '件の取引',
        connectPrompt: 'スワップするにはウォレットを接続してください',
        connected: '接続済み',
        routingOptimized: 'Uniswap V2 · V3 · V4 で最適ルーティング',
        noFees: 'インターフェース手数料なし',
        disclaimer: '暗号資産の売買は自己責任で行ってください。必ずご自身で調査を行ってください。',
        language: '言語',
        metaTitle: 'WiseSwap — マルチバージョン Uniswap ルーター',
        metaDescription: 'WiseSwap — Uniswap V2、V3、V4プールを横断する最適ルーティングでトークンをスワップ。',
    },

    ko: {
        swap: '스왑',
        pools: '풀',
        connect: '연결',
        youPay: '지불',
        youReceive: '수령',
        balance: '잔액',
        enterAmount: '금액 입력',
        selectToken: '토큰 선택',
        swapBtn: '스왑',
        findingRoute: '최적 경로 검색 중...',
        noRouteFound: '경로를 찾을 수 없습니다',
        balanceNotEnough: '잔액 부족',
        connectWallet: '지갑 연결',
        rate: '환율',
        interfaceFee: '인터페이스 수수료',
        feeTooltip: '인터페이스 수수료 없음 — 0 bps',
        priceImpact: '가격 영향',
        minReceived: '최소 수령량',
        networkCost: '네트워크 비용',
        none: '없음',
        settings: '설정',
        slippageTolerance: '슬리피지 허용 범위',
        customPercent: '사용자 지정 %',
        txDeadline: '거래 기한',
        minutes: '분',
        developer: '개발자',
        showTestnets: '테스트넷 표시',
        submittingSwap: '🔄 스왑 전송 중...',
        swapSubmitted: '✅ 스왑 전송 완료!',
        txCount: '건의 거래',
        connectPrompt: '스왑하려면 지갑을 연결하세요',
        connected: '연결됨',
        routingOptimized: 'Uniswap V2 · V3 · V4 최적화 라우팅',
        noFees: '인터페이스 수수료 없음',
        disclaimer: '암호화폐 매매는 본인 책임입니다. 항상 직접 조사하세요.',
        language: '언어',
        metaTitle: 'WiseSwap — 멀티버전 Uniswap 라우터',
        metaDescription: 'WiseSwap — Uniswap V2, V3, V4 풀 전반에서 최적 라우팅으로 토큰 스왑.',
    },

    pt: {
        swap: 'Trocar',
        pools: 'Pools',
        connect: 'Conectar',
        youPay: 'Você paga',
        youReceive: 'Você recebe',
        balance: 'Saldo',
        enterAmount: 'Digite um valor',
        selectToken: 'Selecionar token',
        swapBtn: 'Trocar',
        findingRoute: 'Encontrando a melhor rota...',
        noRouteFound: 'Nenhuma rota encontrada',
        balanceNotEnough: 'Saldo insuficiente',
        connectWallet: 'Conectar carteira',
        rate: 'Taxa',
        interfaceFee: 'Taxa de interface',
        feeTooltip: 'Sem taxa de interface — 0 bps',
        priceImpact: 'Impacto no preço',
        minReceived: 'Mín. recebido',
        networkCost: 'Custo de rede',
        none: 'Nenhuma',
        settings: 'Configurações',
        slippageTolerance: 'Tolerância de derrapagem',
        customPercent: 'Personalizado %',
        txDeadline: 'Prazo da transação',
        minutes: 'minutos',
        developer: 'Desenvolvedor',
        showTestnets: 'Mostrar Testnets',
        submittingSwap: '🔄 Enviando transação...',
        swapSubmitted: '✅ Troca enviada!',
        txCount: 'transação(ões)',
        connectPrompt: 'Conecte sua carteira para trocar',
        connected: 'Conectado',
        routingOptimized: 'Roteamento otimizado via Uniswap V2 · V3 · V4',
        noFees: 'Sem taxas de interface',
        disclaimer: 'Compre e venda criptomoedas por sua conta e risco. Sempre faça sua própria pesquisa.',
        language: 'Idioma',
        metaTitle: 'WiseSwap — Roteador Multi-Versão Uniswap',
        metaDescription: 'WiseSwap — troque tokens com roteamento otimizado através de pools Uniswap V2, V3 e V4.',
    },

    ru: {
        swap: 'Обмен',
        pools: 'Пулы',
        connect: 'Подключить',
        youPay: 'Вы платите',
        youReceive: 'Вы получаете',
        balance: 'Баланс',
        enterAmount: 'Введите сумму',
        selectToken: 'Выберите токен',
        swapBtn: 'Обменять',
        findingRoute: 'Поиск лучшего маршрута...',
        noRouteFound: 'Маршрут не найден',
        balanceNotEnough: 'Недостаточно средств',
        connectWallet: 'Подключить кошелёк',
        rate: 'Курс',
        interfaceFee: 'Комиссия интерфейса',
        feeTooltip: 'Без комиссии интерфейса — 0 бп',
        priceImpact: 'Влияние на цену',
        minReceived: 'Мин. получено',
        networkCost: 'Стоимость сети',
        none: 'Нет',
        settings: 'Настройки',
        slippageTolerance: 'Допуск проскальзывания',
        customPercent: 'Свой %',
        txDeadline: 'Срок транзакции',
        minutes: 'минут',
        developer: 'Разработчик',
        showTestnets: 'Показать Testnets',
        submittingSwap: '🔄 Отправка транзакции...',
        swapSubmitted: '✅ Обмен отправлен!',
        txCount: 'транзакций',
        connectPrompt: 'Подключите кошелёк для обмена',
        connected: 'Подключено',
        routingOptimized: 'Маршрутизация оптимизирована через Uniswap V2 · V3 · V4',
        noFees: 'Без комиссий интерфейса',
        disclaimer: 'Покупайте и продавайте криптовалюты на свой страх и риск. Всегда проводите собственное исследование.',
        language: 'Язык',
        metaTitle: 'WiseSwap — Мульти-версионный маршрутизатор Uniswap',
        metaDescription: 'WiseSwap — обмен токенов с оптимальной маршрутизацией через пулы Uniswap V2, V3 и V4.',
    },

    ar: {
        swap: 'تبديل',
        pools: 'أحواض',
        connect: 'اتصال',
        youPay: 'أنت تدفع',
        youReceive: 'أنت تستلم',
        balance: 'الرصيد',
        enterAmount: 'أدخل المبلغ',
        selectToken: 'اختر الرمز',
        swapBtn: 'تبديل',
        findingRoute: 'البحث عن أفضل مسار...',
        noRouteFound: 'لم يتم العثور على مسار',
        balanceNotEnough: 'الرصيد غير كافٍ',
        connectWallet: 'ربط المحفظة',
        rate: 'السعر',
        interfaceFee: 'رسوم الواجهة',
        feeTooltip: 'بدون رسوم واجهة — 0 نقطة أساس',
        priceImpact: 'التأثير على السعر',
        minReceived: 'الحد الأدنى المستلم',
        networkCost: 'تكلفة الشبكة',
        none: 'لا شيء',
        settings: 'الإعدادات',
        slippageTolerance: 'تحمل الانزلاق',
        customPercent: 'مخصص %',
        txDeadline: 'الموعد النهائي للمعاملة',
        minutes: 'دقائق',
        developer: 'المطور',
        showTestnets: 'إظهار شبكات الاختبار',
        submittingSwap: '🔄 إرسال المعاملة...',
        swapSubmitted: '✅ تم إرسال التبديل!',
        txCount: 'معاملة',
        connectPrompt: 'اربط محفظتك للتبديل',
        connected: 'متصل',
        routingOptimized: 'التوجيه الأمثل عبر Uniswap V2 · V3 · V4',
        noFees: 'بدون رسوم واجهة',
        disclaimer: 'اشترِ وبع العملات المشفرة على مسؤوليتك الخاصة. قم دائمًا بإجراء بحثك الخاص.',
        language: 'اللغة',
        metaTitle: 'WiseSwap — موجّه متعدد الإصدارات لـ Uniswap',
        metaDescription: 'WiseSwap — تبديل الرموز مع التوجيه الأمثل عبر أحواض Uniswap V2 و V3 و V4.',
    },

    tr: {
        swap: 'Takas',
        pools: 'Havuzlar',
        connect: 'Bağlan',
        youPay: 'Ödeme',
        youReceive: 'Alım',
        balance: 'Bakiye',
        enterAmount: 'Miktar girin',
        selectToken: 'Token seçin',
        swapBtn: 'Takas Et',
        findingRoute: 'En iyi rota aranıyor...',
        noRouteFound: 'Rota bulunamadı',
        balanceNotEnough: 'Bakiye yetersiz',
        connectWallet: 'Cüzdan Bağla',
        rate: 'Oran',
        interfaceFee: 'Arayüz ücreti',
        feeTooltip: 'Arayüz ücreti yok — 0 bps',
        priceImpact: 'Fiyat etkisi',
        minReceived: 'Min. alınan',
        networkCost: 'Ağ maliyeti',
        none: 'Yok',
        settings: 'Ayarlar',
        slippageTolerance: 'Kayma toleransı',
        customPercent: 'Özel %',
        txDeadline: 'İşlem son tarihi',
        minutes: 'dakika',
        developer: 'Geliştirici',
        showTestnets: 'Testnet göster',
        submittingSwap: '🔄 İşlem gönderiliyor...',
        swapSubmitted: '✅ Takas gönderildi!',
        txCount: 'işlem',
        connectPrompt: 'Takas yapmak için cüzdanınızı bağlayın',
        connected: 'Bağlandı',
        routingOptimized: 'Uniswap V2 · V3 · V4 üzerinden optimize yönlendirme',
        noFees: 'Arayüz ücreti yok',
        disclaimer: 'Kripto para alım satımı kendi riskinize aittir. Her zaman kendi araştırmanızı yapın.',
        language: 'Dil',
        metaTitle: 'WiseSwap — Çok Sürümlü Uniswap Yönlendirici',
        metaDescription: 'WiseSwap — Uniswap V2, V3 ve V4 havuzları arasında en iyi yönlendirme ile token takası.',
    },

    vi: {
        swap: 'Hoán đổi',
        pools: 'Pool',
        connect: 'Kết nối',
        youPay: 'Bạn trả',
        youReceive: 'Bạn nhận',
        balance: 'Số dư',
        enterAmount: 'Nhập số lượng',
        selectToken: 'Chọn token',
        swapBtn: 'Hoán đổi',
        findingRoute: 'Đang tìm lộ trình tốt nhất...',
        noRouteFound: 'Không tìm thấy lộ trình',
        balanceNotEnough: 'Số dư không đủ',
        connectWallet: 'Kết nối ví',
        rate: 'Tỷ giá',
        interfaceFee: 'Phí giao diện',
        feeTooltip: 'Không phí giao diện — 0 bps',
        priceImpact: 'Ảnh hưởng giá',
        minReceived: 'Tối thiểu nhận',
        networkCost: 'Chi phí mạng',
        none: 'Không',
        settings: 'Cài đặt',
        slippageTolerance: 'Dung sai trượt giá',
        customPercent: 'Tùy chỉnh %',
        txDeadline: 'Hạn giao dịch',
        minutes: 'phút',
        developer: 'Nhà phát triển',
        showTestnets: 'Hiện Testnet',
        submittingSwap: '🔄 Đang gửi giao dịch...',
        swapSubmitted: '✅ Hoán đổi đã gửi!',
        txCount: 'giao dịch',
        connectPrompt: 'Kết nối ví để hoán đổi',
        connected: 'Đã kết nối',
        routingOptimized: 'Định tuyến tối ưu qua Uniswap V2 · V3 · V4',
        noFees: 'Không phí giao diện',
        disclaimer: 'Mua bán tiền mã hóa là rủi ro của bạn. Luôn tự nghiên cứu.',
        language: 'Ngôn ngữ',
        metaTitle: 'WiseSwap — Bộ định tuyến đa phiên bản Uniswap',
        metaDescription: 'WiseSwap — hoán đổi token với định tuyến tối ưu qua các pool Uniswap V2, V3 và V4.',
    },
};

// Language display names (in their own language)
const LOCALE_NAMES = {
    en: 'English',
    es: 'Español',
    zh: '中文',
    fr: 'Français',
    de: 'Deutsch',
    ja: '日本語',
    ko: '한국어',
    pt: 'Português',
    ru: 'Русский',
    ar: 'العربية',
    tr: 'Türkçe',
    vi: 'Tiếng Việt',
};

// ─── State ───
let currentLocale = 'en';
const listeners = [];

/**
 * Detect user's browser language and set the locale.
 * Checks localStorage override first, then navigator.language.
 */
export function initLocale() {
    const saved = localStorage.getItem('wiseswap_locale');
    if (saved && SUPPORTED_LOCALES.includes(saved)) {
        currentLocale = saved;
    } else {
        const browserLang = (navigator.language || navigator.userLanguage || 'en').split('-')[0].toLowerCase();
        currentLocale = SUPPORTED_LOCALES.includes(browserLang) ? browserLang : 'en';
    }
    updateDocumentMeta();
}

/**
 * Get a translated string by key.
 * Falls back to English if the key isn't found in the current locale.
 */
export function t(key) {
    return translations[currentLocale]?.[key] || translations.en[key] || key;
}

/**
 * Get the current locale code
 */
export function getLocale() {
    return currentLocale;
}

/**
 * Change locale and notify all listeners
 */
export function setLocale(locale) {
    if (!SUPPORTED_LOCALES.includes(locale)) return;
    currentLocale = locale;
    localStorage.setItem('wiseswap_locale', locale);
    updateDocumentMeta();
    listeners.forEach(fn => fn(locale));
}

/**
 * Register a callback for locale changes
 */
export function onLocaleChange(fn) {
    listeners.push(fn);
}

/**
 * Update <html lang>, <title>, and <meta> description
 */
function updateDocumentMeta() {
    document.documentElement.lang = currentLocale;
    document.title = t('metaTitle');
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.content = t('metaDescription');
}

export { SUPPORTED_LOCALES, LOCALE_NAMES };
